// client/src/components/pedidos/PedidosPage.js
import React, { Fragment, useState, useMemo, useCallback } from 'react';
import { usePedidos, useMutatePedidos, usePedidosEstatisticasBasicas } from '../../hooks/usePedidos';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';
import PedidoForm from './PedidoForm';
import PedidoRow from './PedidoRow';
import FiltroPedidos from './FiltroPedidos';
import {
    PlusCircle,
    Filter as FilterIcon,
    Loader2,
    RefreshCcw,
    Package,
    XCircle,
    DollarSign,
    AlertCircle,
    AlertTriangle,
    Truck,
    Eye,
    Trash2,
    ArrowRightCircle,
} from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

// Espelha NEXT_STATUS/STATUS_LABEL de mobile/app/pedidos/[id].tsx — mesma
// esteira de status usada no botão "Avançar status" do app mobile.
const STATUS_LABEL = {
    pendente: 'Pendente',
    confirmado: 'Confirmado',
    preparando: 'Preparando',
    pronto_entrega: 'Pronto para Entrega',
    enviado: 'Enviado',
    entregue: 'Entregue',
    cancelado: 'Cancelado',
    reembolsado: 'Reembolsado',
};

const NEXT_STATUS = {
    pendente: 'confirmado',
    confirmado: 'preparando',
    preparando: 'pronto_entrega',
    pronto_entrega: 'enviado',
    enviado: 'entregue',
};

const CANCELABLE = ['pendente', 'confirmado', 'preparando', 'pronto_entrega', 'enviado'];

const Pedidos = () => {
    // ✅ ACHADO: KPI fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const { currency, locale } = useTenantI18n();
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState({});
    const [currentPedido, setCurrentPedido] = useState(null);
    const [selectedPedido, setSelectedPedido] = useState(null);
    const pedidosPerPage = 10;

    const queryParams = useMemo(() => ({
        page: currentPage, 
        limit: pedidosPerPage, 
        ...activeFilters,
        populate: 'cliente,itens.produto,vendaRelacionada,pagamentos'
    }), [currentPage, activeFilters]);

    const { data: pedidosData, isLoading, refetch, error } = usePedidos(queryParams);
    const { deletePedido, cancelarPedido, updatePedido } = useMutatePedidos();
    const isDeleting = deletePedido.isPending;
    const isCanceling = cancelarPedido.isPending;
    const isUpdatingStatus = updatePedido.isPending;
    
    const estatisticas = usePedidosEstatisticasBasicas('hoje');

    const pedidos = pedidosData?.data || [];
    const totalPedidos = pedidosData?.count || 0;
    const pagination = pedidosData?.pagination || {};
    
    const handleApplyFilters = useCallback((filters) => { 
        setCurrentPage(1); 
        setActiveFilters(filters); 
    }, []);

    const handleAdicionarClick = useCallback(() => { 
        setCurrentPedido(null); 
        setIsFormVisible(true); 
    }, []);

    const handleCloseForm = useCallback(() => { 
        setIsFormVisible(false); 
        setCurrentPedido(null); 
    }, []);

    const handleDelete = useCallback((pedidoId) => {
        if (window.confirm('Tem certeza que deseja excluir este pedido?')) {
            deletePedido.mutate(pedidoId);
        }
        setSelectedPedido(null);
    }, [deletePedido]);

    const handleCancelar = useCallback((pedidoId, motivo) => {
        if (window.confirm('Tem certeza que deseja cancelar este pedido?')) {
            cancelarPedido.mutate({ id: pedidoId, motivo });
        }
        setSelectedPedido(null);
    }, [cancelarPedido]);

    const handleAvancarStatus = useCallback((pedido) => {
        const proximoStatus = NEXT_STATUS[pedido.status];
        if (!proximoStatus) return;
        updatePedido.mutate({ id: pedido._id, pedidoData: { status: proximoStatus } });
        setSelectedPedido(null);
    }, [updatePedido]);

    const handleSaveSuccess = useCallback(() => {
        handleCloseForm();
        refetch();
    }, [handleCloseForm, refetch]);

    const handleEditClick = useCallback((pedido) => {
        setCurrentPedido(pedido);
        setIsFormVisible(true);
        setSelectedPedido(null);
    }, []);

    if (error) {
        // .error-state não tinha CSS em lugar nenhum do projeto — mesmo
        // problema já corrigido em OfertasPage.js. Padrão table-error-state.
        return (
            <div className="page-management-container">
                <div className="table-error-state">
                    <AlertTriangle size={48} />
                    <h2>Erro ao carregar pedidos</h2>
                    <p>{error.message || 'Ocorreu um erro ao buscar os pedidos.'}</p>
                    <button
                        onClick={() => refetch()}
                        className="action-button action-button-primary"
                    >
                        <RefreshCcw size={18} className="icon"/>
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Package />}
                    title="Sistema de Pedidos"
                    actions={<>
                        {!isFormVisible && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isLoading}
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {isFormVisible && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary" disabled={isLoading}>
                                <XCircle size={18} className="icon"/> Fechar
                            </button>
                        )}
                    </>}
                />

                {isFormVisible && (
                    <div className="content-panel">
                        <PedidoForm 
                            key={currentPedido?._id || 'new'}
                            onCloseForm={handleCloseForm}
                            onSaveSuccess={handleSaveSuccess}
                            current={currentPedido}
                        />
                    </div>
                )}

                {showFilters && !isFormVisible && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtros</h3>
                        <FiltroPedidos onApplyFilters={handleApplyFilters} loading={isLoading} />
                    </div>
                )}
            </div>

            {!isFormVisible && (
                <Fragment>
                    {/* CARDS DE ESTATÍSTICAS */}
                    <div className="dashboard-grid" style={{ marginTop: '1.5rem' }}>
                        <div className="kpi-card">
                            <div className="kpi-card__icon"><Package size={28} /></div>
                            <h5 className="kpi-card__title">Total de Pedidos</h5>
                            <p className="kpi-card__value">
                                {estatisticas.isLoading
                                    ? <Loader2 size={20} className="animate-spin" />
                                    : estatisticas.data.totalPedidos
                                }
                            </p>
                        </div>

                        <div className="kpi-card">
                            <div className="kpi-card__icon"><DollarSign size={28} /></div>
                            <h5 className="kpi-card__title">Faturamento Total</h5>
                            <p className="kpi-card__value">
                                {estatisticas.isLoading
                                    ? <Loader2 size={20} className="animate-spin" />
                                    : formatCurrency(estatisticas.data.faturamentoTotal || 0, currency, locale)
                                }
                            </p>
                        </div>

                        <div className="kpi-card">
                            <div className="kpi-card__icon"><AlertCircle size={28} /></div>
                            <h5 className="kpi-card__title">Pedidos Pendentes</h5>
                            <p className="kpi-card__value">
                                {estatisticas.isLoading
                                    ? <Loader2 size={20} className="animate-spin" />
                                    : estatisticas.data.pedidosPendentes
                                }
                            </p>
                        </div>

                        <div className="kpi-card">
                            <div className="kpi-card__icon"><Truck size={28} /></div>
                            <h5 className="kpi-card__title">Em Entrega</h5>
                            <p className="kpi-card__value">
                                {estatisticas.isLoading
                                    ? <Loader2 size={20} className="animate-spin" />
                                    : estatisticas.data.pedidosEnviados
                                }
                            </p>
                        </div>
                    </div>

                    {/* TABELA DE PEDIDOS */}
                    <div className="table-container">
                        <div className="table-wrapper">
                            {isLoading && pedidos.length === 0 ? (
                                <div className="table-loading-state">
                                    <Loader2 size={24} className="animate-spin" />
                                </div>
                            ) : pedidos.length > 0 ? (
                                <table className="styled-table">
                                    <thead>
                                        <tr>
                                            <th>Nº Pedido</th>
                                            <th>Cliente</th>
                                            <th>Data</th>
                                            <th>Itens</th>
                                            <th>Total</th>
                                            <th>Status</th>
                                            <th>Tipo</th>
                                            <th>Entrega</th>
                                            <th className="text-right">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pedidos.map(pedido => (
                                            <PedidoRow
                                                key={pedido._id}
                                                pedido={pedido}
                                                onOpenActions={setSelectedPedido}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div className="table-empty-state">
                                    <Package size={64} className="text-gray-300 mb-4" />
                                    <h3 className="empty-title">Nenhum pedido encontrado</h3>
                                    <p className="empty-description">
                                        {Object.keys(activeFilters).length > 0 
                                            ? 'Tente ajustar os filtros para ver mais resultados.'
                                            : 'Comece criando seu primeiro pedido.'
                                        }
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </Fragment>
            )}
            
            {!isFormVisible && totalPedidos > pedidosPerPage && !isLoading && (
                <Pagination
                    {...pagination}
                    totalItems={totalPedidos}
                    onPageChange={setCurrentPage}
                    currentPage={currentPage}
                />
            )}

            <FabButton onClick={handleAdicionarClick} title="Adicionar Pedido" visible={!isFormVisible} disabled={isLoading} icon={<Package size={26} className="icon"/>} />

            <ActionsDrawer
                open={!!selectedPedido}
                onClose={() => setSelectedPedido(null)}
                title={selectedPedido?.numero ? `Pedido Nº ${selectedPedido.numero}` : 'Pedido'}
                subtitle={selectedPedido?.cliente?.user?.name}
                actions={selectedPedido ? [
                    {
                        key: 'editar',
                        label: 'Editar Pedido',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedPedido),
                    },
                    ...(NEXT_STATUS[selectedPedido.status] ? [{
                        key: 'avancar-status',
                        label: `Avançar para ${STATUS_LABEL[NEXT_STATUS[selectedPedido.status]]}`,
                        icon: <ArrowRightCircle size={20} className="icon" />,
                        onClick: () => handleAvancarStatus(selectedPedido),
                        loading: isUpdatingStatus,
                    }] : []),
                    ...(CANCELABLE.includes(selectedPedido.status) ? [{
                        key: 'cancelar',
                        label: 'Cancelar Pedido',
                        icon: <XCircle size={20} className="icon" />,
                        onClick: () => handleCancelar(selectedPedido._id, 'Cancelado pelo usuário'),
                        destructive: true,
                        loading: isCanceling,
                    }] : []),
                    ...(selectedPedido.status === 'pendente' ? [{
                        key: 'excluir',
                        label: 'Excluir Pedido',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedPedido._id),
                        destructive: true,
                        loading: isDeleting,
                    }] : []),
                ] : []}
            />
        </Fragment>
    );
};

export default Pedidos;